/**
 * Zod input schemas: absence. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
// ===========================================================================
// ABSENCE MANAGEMENT
// ===========================================================================
export const ISODateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
export const AbsenceCreateRequestSchema = z
    .object({
    type: z.string().trim().min(1).max(60),
    startDate: ISODateSchema,
    endDate: ISODateSchema,
    halfDayStart: z.boolean().optional(),
    halfDayEnd: z.boolean().optional(),
    /** Single-day half-day only: which half of the day is taken. */
    singleDayHalf: z.enum(['first', 'second']).optional(),
    /** Special leave only: the requested kind from the per-entity catalog. */
    specialLeaveKind: z.string().trim().max(120).optional(),
    /** Sick leave past the certificate-free run: how HR gets the certificate. */
    sickCertMode: z.enum(['upload_later', 'digital']).optional(),
    reason: z.string().trim().max(1000).optional(),
})
    .refine((v) => v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'End date cannot be before the start date.',
});
/** Upload a sick-leave certificate for a request (owner or hub-admin). */
export const AbsenceSickCertUploadSchema = z.object({
    requestId: z.string().min(1),
    filename: z.string().trim().min(1).max(200),
    mimeType: z.string().trim().min(1).max(120),
    /** Base64 file content (no data: prefix), ≤ ~12 MB encoded. */
    contentBase64: z.string().min(1).max(16_000_000),
});
export const AbsenceDecideSchema = z.object({
    requestId: z.string().min(1),
    decision: z.enum(['approved', 'rejected']),
    note: z.string().trim().max(1000).optional(),
});
export const AbsenceCancelSchema = z.object({
    requestId: z.string().min(1),
});
/** HR override (Oct 2026): `withdraw` = an APPROVED request → rejected; `remove` = a
 *  SUBMITTED (not yet decided) request → cancelled. The note goes to the employee. */
export const AbsenceHrOverrideSchema = z.object({
    requestId: z.string().min(1),
    action: z.enum(['withdraw', 'remove']),
    note: z.string().trim().min(1, 'A note for the employee is required').max(1000),
});
/** HR (Oct 2026): recount the working days of every open or future request against the
 *  holidays as configured now (company-wide days, calendar edits). `dryRun` only reports. */
export const AbsenceRecountWorkingDaysSchema = z.object({
    dryRun: z.boolean().nullish(),
});
export const AbsenceTeamCalendarSchema = z
    .object({
    from: ISODateSchema,
    to: ISODateSchema,
    /** Optional: restrict to one person (must be within the caller's scope). */
    ownerEmail: z.string().trim().toLowerCase().email().optional(),
})
    .refine((v) => v.to >= v.from, {
    path: ['to'],
    message: 'End date cannot be before the start date.',
});
/** Team "Member view" — the personal-style overview for one report. */
export const AbsenceMemberOverviewSchema = z.object({
    /** Absent → only the people-in-scope list is returned (for the selector). */
    email: z.string().trim().toLowerCase().email().optional(),
});
/** Approvals-queue fetch — `status` filters the returned requests (default
 *  'submitted' = the open queue; decided/cancelled power the history filters). */
export const AbsencePendingApprovalsSchema = z.object({
    status: z.enum(['submitted', 'approved', 'rejected', 'cancelled']).optional(),
});
// ── DAAily Drivers (peer recognition) ──
/** Assign (or, with remove:true, unassign) a user to a leave policy, optionally
 *  scheduled for a future effective date. */
export const AdminAssignAbsencePolicySchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    policyId: z.string().trim().min(1).max(60),
    effectiveFrom: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    remove: z.boolean().optional(),
});
/** HR policy assignment (hub admins): add (from/to/days/note) or remove one policy on a user. */
export const AdminAssignHrPolicySchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    policyId: z.string().trim().min(1).max(60),
    from: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    to: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    days: z.number().min(0).max(1000).nullish(),
    note: z.string().trim().max(500).nullish(),
    remove: z.boolean().optional(),
});
/** HR records an absence against a person's HR policy (approved on creation). */
export const AbsenceRecordHrPolicySchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    policyId: z.string().trim().min(1).max(60),
    startDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/),
    endDate: z.string().trim().regex(/^\d{4}-\d{2}-\d{2}$/),
    note: z.string().trim().max(500).nullish(),
});
/** HR sets a person's carry-over rule and/or records the carry-over for a leave year (Oct 2026). */
export const AbsenceCarryOverSetSchema = z.object({
    email: z.string().trim().toLowerCase().email(),
    rule: z.object({ mode: z.enum(['none', 'max', 'review']), maxDays: z.number().min(0).max(365).nullable().optional() }).optional(),
    carryOver: z.object({ year: z.number().int().min(2000).max(2100), days: z.number().min(-365).max(365), note: z.string().trim().max(300).nullish() }).optional(),
});
//# sourceMappingURL=absence.js.map